import { useState, useMemo, useEffect, useCallback } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { PlusLg } from 'react-bootstrap-icons';
import axios from 'axios';
import { LeadCard } from '../../Marketplace/LeadCard';
import { LeadDetail } from '../../Marketplace/LeadDetail';
import { FilterSidebar } from '../../Marketplace/FilterSidebar';
import { PostLeadModal } from '../../Marketplace/PostLeadModal';
import { getMaterialName } from '../../Model/BodyMap';

// Transform API lead to frontend format
function transformLead(apiLead) {
  return {
    id: apiLead.id,
    photoUrl: apiLead.photoUrl,
    status: apiLead.status,
    accidentType: apiLead.accidentTypeName || 'Unknown',
    injuries: (apiLead.injuries || []).map(injury => ({
      materialName: getMaterialName(injury.bodyPart),
      bodyPart: injury.bodyPart,
      injuryType: injury.injuryType,
      severity: injury.severity
    })),
    location: {
      city: apiLead.city || '',
      state: apiLead.state
    },
    estimatedValue: apiLead.price || 0,
    postedAt: apiLead.createdAt,
    contact: {
      firstName: apiLead.firstName,
      lastName: apiLead.lastName?.charAt(0) + '.' || '',
      phone: apiLead.phone
    }
  };
}

export function AdminMarketplace({ idToken, isAdmin, purchased = false }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    state: '',
    severityRange: '',
    accidentType: ''
  });
  const [showPostModal, setShowPostModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_CORE_API}/lead${purchased ? '?status=Sold' : ''}`,
        { headers: { Authorization: idToken } }
      );
      const apiLeads = response.data.value || response.data || [];
      // Admin gets every lead back; the marketplace only shows what's been posted (quote requests live on their own page)
      const marketplaceLeads = apiLeads.filter(l => l.status === 'ForSale' || l.status === 'Sold' || l.status === 'Closed');
      setLeads(marketplaceLeads.map(transformLead));
      setError(null);
    } catch (err) {
      console.error('Failed to fetch leads:', err);
      setError('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [idToken, purchased]);

  useEffect(() => {
    if (idToken) {
      fetchLeads();
    }
  }, [idToken, fetchLeads]);

  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      if (filters.state && lead.location.state !== filters.state) {
        return false;
      }

      if (filters.severityRange) {
        const [min, max] = filters.severityRange.split('-').map(Number);
        const maxSeverity = Math.max(...(lead.injuries.map(i => i.severity) || [0]));
        if (maxSeverity < min || maxSeverity > max) return false;
      }

      if (filters.accidentType && lead.accidentType !== filters.accidentType) {
        return false;
      }

      return true;
    });
  }, [leads, filters]);

  const clearFilters = () => {
    setFilters({ state: '', severityRange: '', accidentType: '' });
  };

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
  };

  const handleBack = () => {
    setSelectedLead(null);
  };

  // Show lead detail view if a lead is selected
  if (selectedLead) {
    return (
      <LeadDetail
        lead={selectedLead}
        onBack={handleBack}
        idToken={idToken}
        isAdmin={isAdmin}
        alreadyPurchased={purchased}
        onPurchase={fetchLeads}
        onDelete={(id) => setLeads(prev => prev.filter(l => l.id !== id))}
      />
    );
  }

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">{purchased ? 'Purchased Leads' : 'Leads Marketplace'}</h1>
        <p className="admin-page-description">
          {purchased
            ? 'Leads you have purchased, with full contact details'
            : isAdmin ? 'Manage and view all marketplace leads' : 'Browse available leads in your area'}
        </p>
      </div>

      <FilterSidebar
        filters={filters}
        setFilters={setFilters}
        onClear={clearFilters}
      />

      <div className="mb-3 d-flex justify-content-between align-items-center">
        <span className="text-muted small">
          Showing {filteredLeads.length} lead{filteredLeads.length !== 1 ? 's' : ''}
        </span>
        {isAdmin && (
          <Button variant="outline-primary" size="sm" onClick={() => setShowPostModal(true)}>
            <PlusLg size={14} className="me-1" />
            Post a Lead
          </Button>
        )}
      </div>

      {loading ? (
        <div className="admin-card" style={{ textAlign: 'center', padding: 'var(--space-12) var(--space-6)' }}>
          <Spinner animation="border" size="sm" className="me-2" />
          Loading leads...
        </div>
      ) : error ? (
        <div className="admin-card" style={{ textAlign: 'center', padding: 'var(--space-12) var(--space-6)' }}>
          <p style={{ color: 'var(--qmi-danger)' }}>{error}</p>
          <Button variant="link" onClick={fetchLeads}>Try again</Button>
        </div>
      ) : filteredLeads.length === 0 ? (
        <div className="admin-card" style={{ textAlign: 'center', padding: 'var(--space-12) var(--space-6)' }}>
          <p style={{ color: 'var(--qmi-gray-500)' }}>No leads match your filters.</p>
          <Button variant="link" onClick={clearFilters}>Clear filters</Button>
        </div>
      ) : (
        <div className="lead-grid">
          {filteredLeads.map(lead => (
            <LeadCard key={lead.id} lead={lead} onClick={handleLeadClick} />
          ))}
        </div>
      )}

      {isAdmin && (
        <PostLeadModal
          show={showPostModal}
          onHide={() => setShowPostModal(false)}
          idToken={idToken}
          onLeadPosted={fetchLeads}
        />
      )}
    </div>
  );
}
